import { useState, useRef } from "react";
import { useFetcher } from "@remix-run/react";
import { useLocalePath } from "@commercekit/remix";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { action } from "~/routes-server/resources/campaign-photos.server";

export const useCampaignForm = () => {
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const fetcher = useFetcher<typeof action>();
  const formRef = useRef<HTMLFormElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [nameValue, setNameValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const [captchaToken, setCaptchaToken] = useState<string | null>(
    process.env.NODE_ENV === "development" ? "dev-bypass" : null
  );

  const isBusy = fetcher.state !== "idle";
  const actionData = fetcher.data;

  const isFormReady =
    nameValue.trim().length > 0 &&
    emailValue.trim().length > 0 &&
    files.length > 0 &&
    consent &&
    !!captchaToken;

  const RESOURCE_ACTION = localePath("/resources/campaign-photos");

  const handleSubmit = (_data: unknown, e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    let blocked = false;

    if (files.length === 0) {
      setFileError($pt("campaign_photo_required_error"));
      blocked = true;
    } else if (files.length > 4) {
      setFileError($pt("campaign_photo_max_4_error"));
      blocked = true;
    } else {
      setFileError(null);
    }

    if (!consent) {
      setConsentError($pt("campaign_consent_error_message"));
      blocked = true;
    } else {
      setConsentError(null);
    }

    if (blocked) return;
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    formData.delete("photos");
    files.forEach((file: File) => formData.append("photos", file, file.name));

    fetcher.submit(formData, {
      method: "post",
      action: RESOURCE_ACTION,
      encType: "multipart/form-data",
    });
  };

  return {
    formRef,
    files,
    setFiles,
    fileError,
    setFileError,
    consent,
    setConsent,
    consentError,
    setConsentError,
    setNameValue,
    setEmailValue,
    captchaToken,
    setCaptchaToken,
    isBusy,
    actionData,
    isFormReady,
    RESOURCE_ACTION,
    handleSubmit,
  };
};
